import {
  FeedArea,
  BareFeedArea,
  BareFeedTemplate,
  FeedContentType,
  FeedContentWidgetType,
  FeedTemplate,
} from '../types/feedTemplate';
import { styleToString, hasOwnProperty } from './utils';
import { Property } from '../types/property';
import {
  BareContentTemplateLegacy,
  EventRundownFilter,
  EventRundownTimeGrouping,
} from '../types/contentTemplate';
import { ObjectId } from 'bson';
import {
  dateToStringLongerNoYear,
  DEFAULT_TIMESTR_FORMAT,
  formatDate,
  normalizeMinutes,
  timeToMinutes,
  timeToString,
} from './time';
import { EventTemplate } from '../types/eventTemplate';
import { VenueTemplate } from '../types/venueTemplate';
import { mapBy, firstOf } from './functional';
import { Time } from '../types/time';
import {
  EventAreaBundle,
  ContentAreaBundle,
  DailyProgram,
  DailyProgramEvent,
} from '../types/dailyProgram';
import { SailingDayBundle } from '../types/sailing';
import { getHourFormat } from './property';
import { BrandDateFormat, BrandTimeGrouping } from '../types/brand';
import { TimeGroupingTranslation } from '../types/translation';

// A special key we use in the rundown FeedArea to store the font size
// FIXME: Hacky
export const RUNDOWN_FONT_STYLE_KEY = 'fontSize';

// In pixels
export const RUNDOWN_DEFAULT_FONT_SIZE = 16;

export const getWidgetHTML = (area: FeedArea) => {
  return `<div  className="${area.className}" style="${styleToString(area.style as any)}">
            ${area.content && area.content.content}
          </div>`;
};

export const generateSailingDayWidgetContentTemplate = (
  property: Property,
  dateFormat: BrandDateFormat,
  date?: Date,
  language?: string
): BareContentTemplateLegacy => {
  const content = formatDate(date || new Date(), language ?? 'en-EN', dateFormat);

  return {
    _id: new ObjectId().toHexString(),
    name: 'date_widget',
    property: property._id,
    content,
    params: {
      widgetType: 'date-widget',
    },
  };
};

export const generateItineraryWidgetContentTemplate = (
  property: Property,
  sailingBundle: SailingDayBundle | undefined
): BareContentTemplateLegacy => {
  const hourFormat = getHourFormat(property);

  const content = `
  
  ${sailingBundle?.sailing.days
    .map(
      d => `
    ${d.locations
      .map(
        l => `
<div class="itinerary-rundown-row">
  <div class="itinerary-rundown-day">
    <div>${dateToStringLongerNoYear(d.date)}</div>
  </div>
  <div class="itinerary-rundown-port">
    <div>${l.name}</div>
  </div>
  <div class="itinerary-rundown-arrival">
    <div>${l.arrival ? timeToString(l.arrival, hourFormat, DEFAULT_TIMESTR_FORMAT) : '-'}</div>
  </div>
  <div class="itinerary-rundown-departure">
    <div>${l.departure ? timeToString(l.departure, hourFormat, DEFAULT_TIMESTR_FORMAT) : '-'}</div>
  </div>
</div>`
      )
      .join('')}`
    )
    .join('')}`;

  return {
    _id: new ObjectId().toHexString(),
    name: 'itinerary_widget',
    property: property._id,
    content,
    params: {
      widgetType: 'itinerary-widget',
    },
  };
};

type FeedAreaType = FeedContentType | FeedContentWidgetType;

/**
 * Helper function to collect children areas recursively
 *
 */
function getAreaChildren(area: BareFeedArea, areaType: FeedAreaType, matches: BareFeedArea[]) {
  if (area.contentType === areaType) {
    matches.push(area);
  }

  if (area.children) {
    for (let child of area.children) {
      getAreaChildren(child as any, areaType, matches);
    }
  }
}

/**
 * Find all the areas in a FeedTemplate that have type: `type`
 *
 */
export function findFeedAreas(feed: BareFeedTemplate, areaType: FeedAreaType): BareFeedArea[] {
  let areas: BareFeedArea[] = [];

  feed.pages.forEach(page => {
    page.areas.forEach(area => {
      getAreaChildren(area, areaType, areas);
    });
  });

  return areas;
}

/**
 * Find all the content templates referenced in the feed
 *
 * NOTE: This is quite hacky, and there's probably a better way of doing it. However, I can't seem
 * to find the specific content areas by `areaType` like in `findFeedArea`, so we look for content:
 * <contentId> instead
 */
export function findFeedContent(feed: BareFeedTemplate): ContentAreaBundle[] {
  let contentIds: ContentAreaBundle[] = [];

  feed.pages.forEach((page, i) => {
    page.areas.forEach(area => {
      getAreaContent(area, i, contentIds);
    });
  });

  return firstOf(contentIds, c => c.contentId);
}

// FIXME: Setting the area type to any, because I don't understand what's going on
function getAreaContent(area: any, pageNum: number, contentIds: ContentAreaBundle[]) {
  if (area.content && area.content.name === 'Custom' && area.content.content) {
    contentIds.push({
      type: 'content',
      pageNum,
      contentId: area.content.content,
      feedArea: area,
    });
  }

  if (area.children) {
    for (let child of area.children) {
      getAreaContent(child, pageNum, contentIds);
    }
  }
}

function getAreaEventContent(area: any, pageNum: number, eventContent: EventAreaBundle[]) {
  if (area.content && area.content.params && area.content.params.eventId && area.content.content) {
    eventContent.push({
      type: 'event',
      pageNum,
      eventId: area.content.params.eventId,
      content: area.content.content,
      feedArea: area,
    });
  }
  if (area.children) {
    for (let child of area.children) {
      getAreaEventContent(child, pageNum, eventContent);
    }
  }
}

export function findFeedEventContent(feed: BareFeedTemplate): EventAreaBundle[] {
  let eventBundles: EventAreaBundle[] = [];

  feed.pages.forEach((page, i) => {
    page.areas.forEach(area => {
      getAreaEventContent(area, i, eventBundles);
    });
  });

  return firstOf(eventBundles, ec => ec.content);
}

/**
 * Given an object of type `EventRundownFilter`, return a list of events from `events` that matches
 * the filtering criteria
 *
 */
interface FilterableRundownEvent {
  _id: string;
  startTime: Time;
  template: EventTemplate;
  venue: VenueTemplate;
}

export function filterRundownEvents<T extends FilterableRundownEvent>(
  events: T[],
  filters: EventRundownFilter
): T[] {
  // Convert the input to a FilterableBareRundownEvent, then call `filterBareRundownEvents`
  let bareEvents = events.map(e => ({
    _id: e._id,
    startTime: e.startTime,
    template: {
      levels: e.template.levels.map(l => l._id),
      segment: e.template.segment ? e.template.segment._id : undefined,
      attributes: e.template.attributes.map(a => a._id),
    },
    venue: e.venue._id,
  }));

  let filteredEvents = filterBareRundownEvents(bareEvents, filters);

  let eventMap = mapBy(filteredEvents, '_id');

  // return the elements in the original array that were filtered
  return events.filter(e => !!eventMap[e._id]);
}

export function filterRundownEventsDailyProgram(
  dailyProgram: DailyProgram,
  filters: EventRundownFilter
): DailyProgramEvent[] {
  // Convert the input to a FilterableBareRundownEvent, then call `filterBareRundownEvents`
  let bareEvents = dailyProgram.events.map(e => {
    const template = dailyProgram.collections.eventTemplates.find(et => et._id === e.templateId)!;

    return {
      _id: e.id,
      startTime: e.startTime,
      template: {
        levels: template.levels,
        segment: template.segment ?? undefined,
        attributes: template.attributes,
      },
      venue: e.venueId,
    };
  });

  let filteredEvents = filterBareRundownEvents(bareEvents, filters);

  let eventMap = mapBy(filteredEvents, '_id');

  // return the elements in the original array that were filtered
  return dailyProgram.events.filter(e => !!eventMap[e.id]);
}

export function filterRundownEventsExportV2Result(
  dailyProgram: DailyProgram,
  filters: EventRundownFilter
): DailyProgramEvent[] {
  // Convert the input to a FilterableBareRundownEvent, then call `filterBareRundownEvents`
  const events = dailyProgram.events;

  const bareEvents = events.map(e => {
    const template = dailyProgram.collections.eventTemplates.find(et => et._id === e.templateId)!;

    return {
      _id: e.id,
      startTime: e.startTime,
      template: {
        levels: template.levels,
        segment: template.segment ?? undefined,
        attributes: template.attributes,
      },
      venue: e.venueId,
    };
  });

  let filteredEvents = filterBareRundownEvents(bareEvents, filters);
  let eventMap = mapBy(filteredEvents, '_id');

  // return the elements in the original array that were filtered
  return events.filter(e => !!eventMap[e.id]);
}

/**
 * Same as `filterRundownEvents / FilterableRundownEvent`, but for bare events
 *
 */
interface FilterableBareRundownEvent {
  _id: string;
  startTime: Time;
  template: {
    segment?: string;
    levels: string[];
    attributes: string[];
  };
  venue: string;
}

export function filterBareRundownEvents<T extends FilterableBareRundownEvent>(
  events: T[],
  filters: EventRundownFilter
): T[] {
  const filteringFunctions: Array<(event: T) => boolean> = [];

  if (filters.attributes && filters.attributes.length > 0) {
    const filterAttrStrings = filters.attributes.map(String);
    filteringFunctions.push(e =>
      (e.template.attributes || []).some(attr => filterAttrStrings.includes(String(attr)))
    );
  }

  if (filters.levels && filters.levels.length > 0) {
    filteringFunctions.push(e =>
      (e.template.levels || []).some(level => filters.levels!.includes(level))
    );
  }

  if (filters.segments && filters.segments.length > 0) {
    filteringFunctions.push(
      e => !!e.template.segment && filters.segments!.includes(e.template.segment)
    );
  }

  if (filters.venues && filters.venues.length > 0) {
    const venueStrings = filters.venues.map(String);
    filteringFunctions.push(e => venueStrings.includes(String(e.venue)));
  }

  // If no filters were applied, return the original array
  if (filteringFunctions.length === 0) return events;

  return events.filter(e => filteringFunctions.every(fn => fn(e)));
}

export interface GroupableRundownEvent {
  startTime: Time;
}

interface GroupedEvents<T> {
  groupName?: string;
  events: T[];
  startTime: number;
  endTime: number;
}

/**
 * Resolves a time grouping's saved name to its translated display name, if the brand has
 * a matching Brand Details time grouping option and an active translation for `lang`.
 * Matching is done by name equality (not id) so that free-text values set via YAML blueprint
 * import - which aren't tied to a Brand Details option id - can still pick up a translation.
 */
export function translateTimeGrouping(
  name: string,
  brandTimeGroupings: BrandTimeGrouping[],
  translationsByRefers: { [refersId: string]: TimeGroupingTranslation },
  lang?: string
): string {
  if (!lang) return name;

  const brandTimeGrouping = brandTimeGroupings.find(tg => tg.name === name);
  if (!brandTimeGrouping) return name;

  const translation = translationsByRefers[brandTimeGrouping._id];
  const transform = translation?.transforms;
  if (!transform || !hasOwnProperty(transform, lang) || !transform[lang].active) return name;

  return transform[lang].delta.name;
}

export function groupRundownEvents<T extends GroupableRundownEvent>(
  events: T[],
  grouping?: EventRundownTimeGrouping[],
  resolveGroupName?: (name: string) => string
): Array<GroupedEvents<T>> {
  // FIXME: This should not be hardcoded, take from the plan instead
  const DAY_START = 6 * 60;
  const DAY_END = 24 * 60;

  // If there isn't any grouping defined, return the same events without a group name
  if (!grouping) {
    return [
      {
        events,
        startTime: 0,
        endTime: 0,
      },
    ];
  }

  // Otherwise, we sort by the start time
  let groupedEvents: Array<GroupedEvents<T>> = grouping.map(gr => ({
    groupName: resolveGroupName ? resolveGroupName(gr.name) : gr.name,
    events: [],
    startTime: normalizeMinutes(gr.startTime - DAY_START) % DAY_END,
    endTime: DAY_END,
  }));

  // Sort groups in descending order
  groupedEvents.sort((g1, g2) => g2.startTime - g1.startTime);

  // Assign the endTime for the groups
  let endTime = DAY_END;
  groupedEvents.forEach(g => {
    g.endTime = endTime;
    endTime = g.startTime;
  });

  // Distribute the events on each group based on their start time
  events.forEach(e => {
    let startTime = timeToMinutes(e.startTime, DAY_START);
    groupedEvents.forEach(g => {
      if (startTime >= g.startTime && startTime < g.endTime) {
        g.events.push(e);
      }
    });
  });

  // Reverse the groups so they go from earlier to later in the day
  groupedEvents.reverse();

  return groupedEvents.filter(ge => ge.events.length > 0);
}

export function updateFeedTemplateProperty<T extends keyof FeedArea>(
  feedTemplate: FeedTemplate,
  property: T,
  oldValue: any,
  value: FeedArea[T]
): { feedTemplateUpdated: FeedTemplate; changed: boolean } {
  let changed = false;

  function updateAreas(areas: FeedArea[]) {
    for (const area of areas) {
      if (area[property] === oldValue) {
        area[property] = value;
        changed = true;
      }

      if (area.children && area.children.length > 0) {
        updateAreas(area.children);
      }
    }
  }

  for (const page of feedTemplate.pages) {
    if (page.areas) {
      updateAreas(page.areas);
    }
  }

  return { feedTemplateUpdated: feedTemplate, changed };
}
